import { ArrowLeft, ChevronRight } from "lucide-react";
import { useCallback, useMemo } from "react";
import { strings } from "@/I18n";
import type { PanelEntityType, PanelEntry } from "@/v2/stores/panelStore";
import { usePanelStore, usePanelTopEntry } from "@/v2/stores/panelStore";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/v2/ui/primitives/sheet";
import { EntityPanelRouter } from "./EntityPanelRouter";
import { PanelContextProvider } from "./PanelContext";

function getFullPageUrl(entityType: PanelEntityType, entityId: string): string {
  switch (entityType) {
    case "vehicle":
      return `/v2/dashboard/bike/${entityId}`;
    case "station":
      return `/v2/dashboard/stations?search=${encodeURIComponent(entityId)}`;
    case "dock":
      return `/v2/dashboard/docks?search=${encodeURIComponent(entityId)}`;
    case "user": {
      const [userId, cityId] = entityId.split("-");
      return `/v2/dashboard/customer/${userId}/${cityId}`;
    }
    case "rental":
      return `/v2/dashboard/rentals?search=${encodeURIComponent(entityId)}`;
    case "transaction":
      return `/v2/dashboard/transaction/${entityId}`;
    case "reservation":
      return "/v2/dashboard/reservations";
    case "city":
      return "/dashboard/cities";
    case "lock":
      return `/v2/dashboard/locks?search=${encodeURIComponent(entityId)}`;
    case "vehicleModel":
      return `/v2/dashboard/vehicle-models/${entityId}`;
    default:
      return "/v2/dashboard";
  }
}

function getEntityLabel(entityType: PanelEntityType): string {
  switch (entityType) {
    case "vehicle":
      return strings("table.vehicle");
    case "station":
      return strings("table.station");
    case "dock":
      return strings("table.dock");
    case "user":
      return strings("table.user");
    case "rental":
      return strings("table.rental");
    case "transaction":
      return strings("table.transaction");
    case "reservation":
      return "Reservation";
    case "city":
      return strings("table.city");
    case "lock":
      return strings("table.lock");
    case "vehicleModel":
      return strings("table.vehicleModel");
    case "attention":
      return strings("overviewScreen.needsAttention");
  }
}

function BreadcrumbItem({
  entry,
  index,
  isLast,
  popToIndex,
}: {
  entry: PanelEntry;
  index: number;
  isLast: boolean;
  popToIndex: (index: number) => void;
}) {
  const label = entry.label || getEntityLabel(entry.entityType);
  return isLast ? (
    <span className="tw-text-xs tw-text-muted-foreground tw-truncate">
      {label}
    </span>
  ) : (
    <button
      type="button"
      onClick={() => popToIndex(index)}
      className="tw-text-xs tw-text-foreground hover:tw-underline tw-truncate"
    >
      {label}
    </button>
  );
}

function Breadcrumbs({
  stack,
  popToIndex,
}: {
  stack: PanelEntry[];
  popToIndex: (index: number) => void;
}) {
  const crumbs: React.ReactNode[] = [];

  if (stack.length < 4) {
    stack.forEach((entry, index) => {
      if (index > 0) {
        crumbs.push(
          <ChevronRight
            key={`sep-${index}-${entry.entityType}-${entry.entityId}`}
            className="tw-h-3 tw-w-3 tw-text-muted-foreground tw-flex-shrink-0"
          />
        );
      }
      crumbs.push(
        <BreadcrumbItem
          key={`${entry.entityType}:${entry.entityId}`}
          entry={entry}
          index={index}
          isLast={index === stack.length - 1}
          popToIndex={popToIndex}
        />
      );
    });
  } else {
    crumbs.push(
      <BreadcrumbItem
        key={`${stack[0].entityType}:${stack[0].entityId}`}
        entry={stack[0]}
        index={0}
        isLast={false}
        popToIndex={popToIndex}
      />
    );

    crumbs.push(
      <ChevronRight
        key="sep-ellipsis-l"
        className="tw-h-3 tw-w-3 tw-text-muted-foreground tw-flex-shrink-0"
      />
    );
    crumbs.push(
      <span key="ellipsis" className="tw-text-xs tw-text-muted-foreground">
        …
      </span>
    );

    const penIdx = stack.length - 2;
    crumbs.push(
      <ChevronRight
        key="sep-pen"
        className="tw-h-3 tw-w-3 tw-text-muted-foreground tw-flex-shrink-0"
      />
    );
    crumbs.push(
      <BreadcrumbItem
        key={`${stack[penIdx].entityType}:${stack[penIdx].entityId}`}
        entry={stack[penIdx]}
        index={penIdx}
        isLast={false}
        popToIndex={popToIndex}
      />
    );

    const lastIdx = stack.length - 1;
    crumbs.push(
      <ChevronRight
        key="sep-last"
        className="tw-h-3 tw-w-3 tw-text-muted-foreground tw-flex-shrink-0"
      />
    );
    crumbs.push(
      <BreadcrumbItem
        key={`${stack[lastIdx].entityType}:${stack[lastIdx].entityId}`}
        entry={stack[lastIdx]}
        index={lastIdx}
        isLast={true}
        popToIndex={popToIndex}
      />
    );
  }

  return <>{crumbs}</>;
}

export function EntityPanel() {
  const topEntry = usePanelTopEntry();
  const { stack, isOpen, closePanel, popPanel, popToIndex } = usePanelStore();
  const showBreadcrumbs = stack.length > 1;

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) closePanel();
    },
    [closePanel]
  );

  const handleOpenFullPage = useCallback(() => {
    if (!topEntry) return;
    window.open(
      getFullPageUrl(topEntry.entityType, topEntry.entityId),
      "_blank"
    );
  }, [topEntry]);

  const title = useMemo(() => {
    if (!topEntry) return "";
    return topEntry.label || getEntityLabel(topEntry.entityType);
  }, [topEntry]);

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        overlayClassName="tw-bg-black/10"
        className="tw-w-full sm:tw-w-[560px] tw-p-0 tw-flex tw-flex-col"
      >
        <SheetTitle className="tw-sr-only">{title}</SheetTitle>
        <SheetDescription className="tw-sr-only">
          {strings("panel.detailsFor", { title })}
        </SheetDescription>

        {showBreadcrumbs && (
          <div className="tw-flex tw-items-center tw-gap-1 tw-px-4 tw-pt-3 tw-pb-1 tw-min-w-0 tw-border-b tw-border-border tw-flex-shrink-0">
            <button
              type="button"
              onClick={popPanel}
              className="tw-p-1 tw-rounded hover:tw-bg-muted tw-text-muted-foreground tw-flex-shrink-0"
              aria-label={strings("common.back")}
            >
              <ArrowLeft className="tw-h-4 tw-w-4" />
            </button>
            <Breadcrumbs stack={stack} popToIndex={popToIndex} />
          </div>
        )}

        <div className="tw-flex-1 tw-overflow-y-auto">
          <PanelContextProvider openFullPage={handleOpenFullPage}>
            {topEntry && (
              <EntityPanelRouter
                key={`${topEntry.entityType}:${topEntry.entityId}`}
                entry={topEntry}
              />
            )}
          </PanelContextProvider>
        </div>
      </SheetContent>
    </Sheet>
  );
}
